/**
 * PDFIntakeDropzone — drag-drop a PDF, GPT-5.2 extracts structured fields,
 * optionally confirms / overrides them, then commits + attaches the PDF as
 * evidence on the parent record.
 *
 * Used on:
 *   • Equipment service logs (intake_type='equipment_service')
 *   • Equipment QA records  (intake_type='equipment_qa')
 *   • Equipment install     (intake_type='equipment_install')
 *   • Lead apron tests      (intake_type='lead_apron_test')
 *   • TLD readings          (intake_type='tld_reading')
 *
 * Props:
 *   intakeType   — one of the supported intake types
 *   label        — human label for the drop zone
 *   color        — accent colour
 *   onExtracted(fields, file) — called with the extracted dict + raw File so
 *                  parent can pre-fill its form OR commit immediately and
 *                  attach the PDF as evidence.
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PDFIntakeDropzone({
  intakeType,
  label = 'Drop PDF — auto-fill',
  color = '#42CDD7',
  onExtracted,
  testidPrefix = 'pdf-intake',
}) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);
  const inputRef = useRef(null);

  const handle = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setErr('Only PDF accepted.'); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErr('File exceeds 10 MB.'); return;
    }
    setBusy(true); setErr(null); setSuccess(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/intake/pdf?intake_type=${intakeType}`, fd, { withCredentials: true });
      if (r.data.error) {
        setErr(`AI couldn't parse the PDF: ${r.data.error.slice(0, 140)}`);
      } else {
        const filled = Object.values(r.data.extracted || {}).filter(v => v !== '' && v != null).length;
        setSuccess(`AI auto-filled ${filled} field(s) from ${file.name}`);
        onExtracted?.(r.data.extracted, file);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'Extraction failed');
    }
    setBusy(false);
  };

  return (
    <div data-testid={`${testidPrefix}-block`}>
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files?.[0]); }}
        onClick={() => !busy && inputRef.current?.click()}
        data-testid={`${testidPrefix}-drop`}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-3 text-center text-xs transition ${
          drag ? '' : 'hover:bg-muted/30'
        }`}
        style={{
          borderColor: drag ? color : `${color}50`,
          backgroundColor: drag ? `${color}10` : 'transparent',
          color,
        }}>
        {busy ? (
          <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> AI is reading the PDF…</span>
        ) : (
          <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {label}</span>
        )}
        <input ref={inputRef} type="file" accept=".pdf,application/pdf" className="hidden"
          onChange={e => handle(e.target.files?.[0])}
          data-testid={`${testidPrefix}-input`} />
      </div>

      {err && (
        <div className="mt-2 text-[11px] text-red-600 inline-flex items-start gap-1"
          data-testid={`${testidPrefix}-error`}>
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {success && !err && (
        <div className="mt-2 text-[11px] text-green-700 inline-flex items-start gap-1"
          data-testid={`${testidPrefix}-success`}>
          <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" /> {success}
        </div>
      )}
    </div>
  );
}
